// ==================== CAMPAIGN PROGRESS (LIVE) ====================
// Stepper + counters for a campaign that is running right now. Driven by the
// shared run context (run.started / run.progress / run.completed events, restored
// from the server after a refresh) — no polling.

import { Loader2, CheckCircle2, Search, Sparkles, Target, Send } from 'lucide-react';
import { cn } from '../../../utils/helpers';
import type { RunState } from './LeadGenRunContext';

export const STAGE_LABEL: Record<string, string> = {
  discovery: 'Discovering companies',
  enrichment: 'Enriching & summarizing',
  scoring: 'Filtering & scoring',
  outreach: 'Drafting & sending',
  done: 'Completed',
  failed: 'Failed',
};

const STEPS: { key: string; label: string; icon: React.ElementType }[] = [
  { key: 'discovery', label: 'Discover', icon: Search },
  { key: 'enrichment', label: 'Enrich', icon: Sparkles },
  { key: 'scoring', label: 'Score', icon: Target },
  { key: 'outreach', label: 'Draft & send', icon: Send },
];
const ORDER = STEPS.map((s) => s.key);

function Counter({ label, value, tone }: { label: string; value?: number; tone?: 'orange' | 'success' }) {
  return (
    <div className="min-w-[5.5rem]">
      <p className="text-[10px] uppercase tracking-wider text-(--color-muted-gray)">{label}</p>
      <p className={cn('text-lg font-mono font-bold tabular-nums leading-tight', tone === 'orange' ? 'text-(--color-signal-orange)' : tone === 'success' ? 'text-(--color-success)' : 'text-white')}>
        {value ?? '—'}
      </p>
    </div>
  );
}

export default function CampaignProgress({ run }: { run: RunState }) {
  const idx = run.done ? ORDER.length : Math.max(0, ORDER.indexOf(run.stage));
  const passedTotal = run.passed ?? undefined;
  const evaluated = run.evaluated ?? undefined;
  const pct = run.done
    ? 100
    : run.stage === 'outreach' && passedTotal
      ? Math.min(99, 60 + Math.round(((evaluated ?? 0) / Math.max(1, passedTotal)) * 40))
      : { discovery: 10, enrichment: 30, scoring: 55 }[run.stage] ?? 5;

  return (
    <div className={cn(
      'rounded-lg border p-4 space-y-3',
      run.failed ? 'border-(--color-error)/40 bg-(--color-error-light)' : 'border-(--color-signal-orange)/40 bg-(--color-orange-glow)',
    )}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {run.done ? (
            <CheckCircle2 className={cn('h-4 w-4', run.failed ? 'text-(--color-error)' : 'text-(--color-success)')} />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin text-(--color-signal-orange)" />
          )}
          <p className="text-sm font-semibold text-white">
            {run.failed ? `Campaign failed${run.errorMessage ? ` — ${run.errorMessage}` : ''}` : STAGE_LABEL[run.stage] ?? 'Running'}
            {!run.done && run.stage === 'outreach' && passedTotal ? (
              <span className="text-(--color-muted-gray) font-normal"> · lead {Math.min(evaluated ?? 0, passedTotal)} of {passedTotal}</span>
            ) : null}
          </p>
        </div>
        <p className="text-xs text-(--color-muted-gray)">
          {run.done ? 'Leads below are final.' : 'Leads appear below as they are found — no refresh needed.'}
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const state = i < idx ? 'done' : i === idx && !run.done ? 'active' : run.done ? 'done' : 'todo';
          const Icon = s.icon;
          return (
            <div key={s.key} className="flex items-center gap-2 flex-1 min-w-0">
              <div className={cn(
                'flex items-center gap-1.5 text-xs whitespace-nowrap',
                state === 'active' ? 'text-(--color-signal-orange) font-semibold' : state === 'done' ? 'text-(--color-success)' : 'text-(--color-muted-gray)',
              )}>
                {state === 'done' ? <CheckCircle2 className="h-3.5 w-3.5" /> : state === 'active' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
                {s.label}
              </div>
              {i < STEPS.length - 1 && <div className={cn('h-px flex-1', state === 'done' ? 'bg-(--color-success)/50' : 'bg-(--color-border-gray)/60')} />}
            </div>
          );
        })}
      </div>
      <div className="h-1.5 rounded-full bg-(--color-border-gray)/50 overflow-hidden">
        <div className={cn('h-full transition-all duration-700', run.failed ? 'bg-(--color-error)' : 'bg-gradient-to-r from-(--color-signal-orange) to-(--color-warm-amber)')} style={{ width: `${pct}%` }} />
      </div>

      {/* Counters */}
      <div className="flex flex-wrap gap-5">
        <Counter label="Discovered" value={run.discovered} />
        <Counter label="Passed filter" value={run.passed} />
        <Counter label="Qualified" value={run.qualified} tone="orange" />
        <Counter label="Drafts" value={run.drafted ?? run.totalDrafts} />
        <Counter label="Emails sent" value={run.sent} tone="success" />
      </div>
    </div>
  );
}
